import { useParams } from "react-router-dom";
import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Checkbox,
  CircularProgress,
  Button,
  TextField,
  MenuItem,
  FormControlLabel,
  Typography,
  Autocomplete,
} from "@mui/material";

const EditUserForm = ({
  setExpanded,
  setUserUpdated,
  setUserError,
  setOpen,
  setMessage,
  setAlertColour,
}) => {
  const { user } = useParams();
  const [userData, setUserData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [subData, setUserSub] = useState([]);
  const [submit, setSubmit] = useState(false);
  const [orgsList, setOrgsList] = useState([]);
  const [subChange, setSubChange] = useState(false);

  const [allEmails, setAllEmails] = useState([])

  useEffect(() => {
    const fetchData = async () => {
      const respUser = await axios.get(`/userRec/${user}`);
      const respSub = await axios.get(`/userSub/${user}`);
      const users = await axios.get(`/users`);

      setAllEmails(users.data.users.map((user)=>user.email));

      setUserData(respUser.data.user);
      setUserSub(respSub.data.subscription);
    };

    fetchData().then(() => setIsLoading(false));
  }, [submit]);

  const initialValues = {
    email: userData.email,
    firstName: userData.firstName,
    lastName: userData.lastName,
    inputIPAddress: userData.ipAddress,
    mfaKey: userData.mfaKey,
    inputStartDate: dayjs(userData.startDate).toISOString().slice(0, 10),
    inputEndDate: dayjs(userData.endDate).toISOString().slice(0, 10),
    inputState: userData.state,
    username: userData.username,
    inputKey: subData.keyId,
    company: subData.orgName,
    inputAcType: userData.accountType,
    inputGeneric: userData.genAccount,
  };

  const userSchema = yup.object().shape({
    firstName: yup
      .string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    lastName: yup
      .string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required"),
    email: yup.string().email("Invalid email").required("Required")
    .transform((value)=> value.toLowerCase())
    .notOneOf(allEmails.map((email)=>email.toLowerCase()),
    "Email already exists"
    ),
    username: yup
      .string()
      .min(5, "Too Short!")
      .max(10, "Too Long!")
      .required("Required"),
    inputIPAddress: yup.string().min(9, "Too Short!").max(35, "Too Long!"),
    inputStartDate: yup
      .string()
      .matches(
        /^([1-9]\d{3}|2100)-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
        "Invalid date"
      )
      .required("Required"),
      inputEndDate: yup
      .string()
      .matches(
        /^([1-9]\d{3}|2100)-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/,
        "Invalid date"
      )
      .required("Required"),
  });

  const handleFormSubmit = (values) => {

    values.inputStartDate = dayjs(values.inputStartDate)
      .toISOString()
      .slice(0, 10);
    values.inputEndDate = dayjs(values.inputEndDate).toISOString().slice(0, 10);

    axios
      .patch(`/patchUserRec`, { values: values })
      .then((res) => {
        setUserUpdated(true);

        setSubmit(!submit);
        setMessage(res.data);
        setAlertColour("success");
        setExpanded(false);
      })
      .then(() => {
        setOpen(true);
      })
      .catch((err) => {
        setAlertColour("error");
        setMessage(err.response.data);

        setSubmit(!submit);

        setOpen(true);
      });
  };

  const handleSubChangeClick = () => {
    axios
      .get("/orgs")
      .then((res) => {
        setOrgsList(res.data.orgs.filter((org) => dayjs(org.expiry) > dayjs()));
      })
      .then(() => {
        setSubChange(true);
      });
  };


  if (isLoading)
    return (
      <Box display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  else
    return (
      <Box p="20px" sx={{ backgroundColor: "#212121", mt: "5px", mb: "5px" }}>
        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={userSchema}
        >
          {({
            values,
            errors,
            touched,
            resetForm,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            setFieldTouched,
            
            dirty,
          }) => (
            <form onSubmit={handleSubmit} onReset={resetForm}>
              <Box
                display="grid"
                gap="30px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                }}
              >
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="First Name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.firstName}
                  name="firstName"
                  error={!!touched.firstName && !!errors.firstName}
                  helperText={touched.firstName && errors.firstName}
                  sx={{ gridColumn: "span 1" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Last Name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.lastName}
                  name="lastName"
                  error={!!touched.lastName && !!errors.lastName}
                  helperText={touched.lastName && errors.lastName}
                  sx={{ gridColumn: "span 1" }}
                />

                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="username"
                  disabled={true}
                  value={values.username}
                  name="username"
                  sx={{ gridColumn: "span 1" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.email}
                  name="email"
                  error={!!touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  sx={{ gridColumn: "span 2" }}
                />

                <TextField
                  select
                  value={values.inputState}
                  name="inputState"
                  label="State"
                  onChange={handleChange}
                >
                  <MenuItem value={"Active"}>Active</MenuItem>
                  <MenuItem value={"Inactive"}>Inactive</MenuItem>
                  <MenuItem value={"Pending"}>Pending</MenuItem>
                  <MenuItem value={"Expired"}>Expired</MenuItem>
                  <MenuItem value={"Cancelled"}>Cancelled</MenuItem>
                  <MenuItem value={"Payment due"}>Payment due</MenuItem>
                </TextField>

                <TextField
                  select
                  value={values.inputAcType}
                  name="inputAcType"
                  label="Account type"
                  onChange={handleChange}
                >
                  <MenuItem key={"standard)"} value={"standard"}>
                    Standard
                  </MenuItem>
                  <MenuItem key={"free)"} value={"free"}>
                    Free
                  </MenuItem>
                  <MenuItem key={"viewer)"} value={"view"}>
                    Viewer
                  </MenuItem>
                  <MenuItem key={"plus"} value={"plus"}>
                    Plus
                  </MenuItem>
                  {values.inputAcType == "admin" && (
                    <MenuItem key={"admin"} value={"admin"}>
                      Admin
                    </MenuItem>
                  )}
                </TextField>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    label="Start date"
                    value={values.inputStartDate}
                    maxDate={values.inputEndDate}
                    inputFormat="DD/MM/YYYY"
                    name="inputStartDate"
                    onChange={(newValue) => {
                      newValue = newValue.add(12, "hour");
                      setFieldValue(
                        "inputStartDate",
                        newValue.toISOString().slice(0, 10)
                      );
                      setFieldTouched("inputStartDate", true);
                    }}
                    renderInput={(params) => (
                      <TextField
                        sx={{
                          width: "100%",
                        }}
                        {...params}
                        name="inputStartDate"
                        onKeyDown={(e) => e.preventDefault()}
                        error={
                          !!touched.inputStartDate && !!errors.inputStartDate
                        }
                        helperText={
                          touched.inputStartDate && errors.inputStartDate
                        }
                      />
                    )}
                  />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="End Date"
                    value={values.inputEndDate}
                    inputFormat="DD/MM/YYYY"
                    minDate={values.inputStartDate}
                    name="inputEndDate"
                    onChange={(newValue) => {
                      newValue = newValue.add(12, "hour");
                      setFieldValue(
                        "inputEndDate",
                        newValue.toISOString().slice(0, 10)
                      );
                      setFieldTouched("inputEndDate", true);
                    }}
                    renderInput={(params) => (
                      <TextField
                        sx={{
                          width: "100%",
                        }}
                        {...params}
                        name="inputEndDate"
                        onKeyDown={(e) => e.preventDefault()}
                        error={!!touched.inputEndDate && !!errors.inputEndDate}
                        helperText={touched.inputEndDate && errors.inputEndDate}
                      />
                    )}
                  />
                </LocalizationProvider>

                {subChange == false && (
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="company"
                    disabled={true}
                    value={values.company}
                    name="company"
                    sx={{ gridColumn: "span 1" }}
                  />
                )}

                {subChange == true && (
                  <Autocomplete
                    name="company"
                    options={orgsList}
                    getOptionLabel={(option) => option.client}
                    isOptionEqualToValue={(option, value) =>
                      option.id == value.id
                    }
                    onChange={(event, value) => {
                      setFieldValue("inputKey", value.id);
                      setFieldValue("company", value.client);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Choose an company" />
                    )}
                  />
                )}

                <Button onClick={handleSubChangeClick}>Change</Button>

                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="inputGeneric"
                        checked={values.inputGeneric}
                        onChange={() => {
                          setFieldValue("inputGeneric", !values.inputGeneric);
                        }}
                        sx={{ mr: "20px" }}
                      />
                    }
                    label="Generic account?"
                    labelPlacement="start"
                    sx={{ ml: "0", mr: "20px" }}
                  />
                </div>

                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="IP Address"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.inputIPAddress}
                  name="inputIPAddress"
                  error={!!touched.inputIPAddress && !!errors.inputIPAddress}
                  helperText={touched.inputIPAddress && errors.inputIPAddress}
                  sx={{ gridColumn: "span 1" }}
                />
              </Box>
              <Box display="flex" justifyContent="end" mt="20px">
                <Button
                  disabled={!dirty}
                  type="submit"
                  color="secondary"
                  variant="contained"
                  sx={{ ml: "2px", mr: "2px" }}
                >
                  Save Edits
                </Button>
                <Button type="reset" variant="contained" color="secondary">
                  Reset
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    );
};

export default EditUserForm;
